import React, { useEffect, useMemo, useState } from 'react';

import { Chip, ChipList } from '@/components/shared/Chip';
import { Legend, LegendItem } from '@/components/shared/Legend';
import { LineChart } from '@/components/shared/LineChart';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionSecondaryText } from '@/components/shared/ModalSectionSecondaryText';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import {
	getSongThemeColor,
	SongThemeChip,
} from '@/components/shared/SongThemeChip';
import { StreamChart } from '@/components/shared/StreamChart';
import { TeamSongTags } from '@/components/TeamSongTags';
import { TeamSongThumbs } from '@/components/TeamSongThumbs';
import {
	ENERGY_TAGS,
	GOSPEL_TAGS,
	GOSPEL_TAGS_SHORT,
} from '@/config/constants';
import { classNames } from '@/helpers/classNames';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { linearGradientDef } from '@nivo/core';

import AnalysisCard from '../AnalysisCard/AnalysisCard';
import ExampleChartLabel from '../ExampleChartLabel/ExampleChartLabel';
import { demoLineData } from './demoLineData';
import styles from './GospelFlow.module.scss';

const GospelFlow: React.FC<{
	teamSongIds: string[];
	className?: string;
}> = ({ teamSongIds, className }) => {
	const { findTeamSongsById } = useTeamSongs();
	const setlistTeamSongs = findTeamSongsById(teamSongIds);
	const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

	const tags = setlistTeamSongs.map((ts) => ts?.gospelTags);

	const flow = useMemo(() => {
		return tags.reduce<{ [key: string]: number }[]>((acc, t) => {
			const matchingCount = t
				? GOSPEL_TAGS.filter((gt) => t.includes(gt.label)).length
				: 0;
			const matchValue = matchingCount > 0 ? 1 / matchingCount : 0;
			return [
				...acc,
				{
					[GOSPEL_TAGS[0].label]:
						t && t.includes(GOSPEL_TAGS[0].label) ? matchValue : 0,
					[GOSPEL_TAGS[1].label]:
						t && t.includes(GOSPEL_TAGS[1].label) ? matchValue : 0,
					[GOSPEL_TAGS[2].label]:
						t && t.includes(GOSPEL_TAGS[2].label) ? matchValue : 0,
				},
			];
		}, []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tags.join()]);

	return teamSongIds.length > 1 ? (
		<AnalysisCard
			title="Gospel story"
			responsiveArea={<TeamSongThumbs teamSongIds={teamSongIds} />}
		>
			<StreamChart
				wrapperClassName={styles['line-chart']}
				data={flow}
				keys={GOSPEL_TAGS.map((t) => t.label)}
				animate={false}
				hoveredLabel={hoveredLabel}
				onHoverChange={setHoveredLabel}
			/>
			<Legend className={styles['legend']}>
				{GOSPEL_TAGS.map((tag) => (
					<LegendItem
						key={tag.label}
						label={tag.capitalisedLabel}
						className={getSongThemeColor(tag.capitalisedLabel).className}
						onMouseEnter={() => setHoveredLabel(tag.label)}
						onMouseLeave={() => setHoveredLabel(null)}
						dimmed={hoveredLabel !== null && hoveredLabel !== tag.label}
					/>
				))}
			</Legend>
		</AnalysisCard>
	) : (
		<AnalysisCard
			title="Gospel story"
			responsiveArea={
				<ModalSectionSecondaryText>
					Add more songs to see the gospel story flow of your setlist
				</ModalSectionSecondaryText>
			}
		>
			<div className={styles['wrapper']}>
				<StreamChart
					wrapperClassName={classNames([styles['line-chart']])}
					data={demoLineData}
					keys={GOSPEL_TAGS.map((t) => t.label)}
				/>
				<ExampleChartLabel />
			</div>
		</AnalysisCard>
	);
};

export default GospelFlow;
